import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Amplify, Auth, Hub } from 'aws-amplify';
import Routes from './Routes';
import LambdaFetch from './functions/FetchFromLambda';
import AppBar from './components/AppBar';
import { Button } from '@material-ui/core';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useAuth } from './hooks/useAuth';
import { useDynamicTitle } from './hooks/useDynamicTitle';

import './App.css';
import LoginPage from './LoginPage';
import { 
  isPageInRegistry, 
  isSubpageInRegistry, 
  getAllReportCategoryKeys,
  getPageByKey,
  resolveEnabledModules,
  isModuleEnabled
} from './config/permissions-registry';

function AppCognito() {
  const { enqueueSnackbar } = useSnackbar();
  
  const [state, setState] = useState({
    isAuth: false,
    doneTokenCheck: false,
    isGettingProfile: true,
    isInvalidUser: false,
    user: {},
  });
  const [clientModules,setClientModules] = useState(null)
  const [federatedIdName, setFederatedIdName] = useState('Google');
  const [token, setToken] = useState(null);
  const [awsRegion, setAwsRegion] = useState(null);
  const [appVals, setAppVals] = useState({
    appWidth: 1000,
    primaryAppColor: '#1482C5',
    trinaryAppColor: '#0d6eaa',
  });
  const [logoVals, setLogoVals] = useState({
    logoUrl: '12',
    logoHeight: '40px',
    logoHeightSmall: '30px',
    logoBlackUrl: null
  });
  const [systemTimezone, setSystemTimezone] = useState('America/New_York'); // Default timezone

  // Initialize createSnack function
  const createSnack = useCallback((message, type, duration) => {
    enqueueSnackbar(message, {
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      variant: type,
      autoHideDuration: duration,
    });
  }, [enqueueSnackbar]);

  // Centralized permission checker with wildcard ('*') and catch-all ('-1') support
  // Usage: allow('page', 'employee') or allow('subpage', 'HR', 'benefits')
  // Now also checks permissions registry - only allows access if permission exists in registry
  // Also checks if the module is enabled for this client
  const allow = useCallback((...path) => {
    // SECURITY: First check if the permission exists in the registry
    if (path.length === 0) return false
    
    const [type, ...rest] = path
    
    // Get enabled modules from state (clientModules is a separate state variable)
    const rawModules = clientModules;
    const enabledModules = resolveEnabledModules(rawModules);
    
    // Check registry based on permission type
    if (type === 'page' && rest.length > 0) {
      const pageKey = rest[0]
      if (!isPageInRegistry(pageKey)) {
        console.warn(`Page "${pageKey}" is not in permissions registry. Access denied.`)
        return false
      }
      
      // Check if page's module is enabled for this client
      const pageInfo = getPageByKey(pageKey);
      const pageModule = pageInfo?.module;
      if (pageModule && !isModuleEnabled(pageModule, enabledModules)) {
        // Module not enabled - deny access regardless of permissions
        return false;
      }
    } else if (type === 'subpage' && rest.length >= 2) {
      const [module, subpageKey] = rest
      if (!isSubpageInRegistry(module, subpageKey)) {
        console.warn(`Subpage "${subpageKey}" for module "${module}" is not in permissions registry. Access denied.`)
        return false
      }
      
      // Check if module is enabled for this client
      if (!isModuleEnabled(module, enabledModules)) {
        // Module not enabled - deny access
        return false;
      }
    } else if (type === 'reportCategory' && rest.length > 0) {
      const categoryKey = rest[0]
      const validCategories = getAllReportCategoryKeys()
      if (!validCategories.includes(categoryKey)) {
        console.warn(`Report category "${categoryKey}" is not in permissions registry. Access denied.`)
        return false
      }
      
      // Report categories are keyed by module - check if module is enabled
      if (!isModuleEnabled(categoryKey, enabledModules)) {
        // Module not enabled - deny access
        return false;
      }
    }
    // Note: Other permission types (like 'module', 'company') can be added here if needed
    
    // After registry and module checks pass, check user's roleActions
    let cur = state?.user?.roleActions
    // Root-wide boolean grants everything. Avoid short-circuiting on root '*' to allow deeper explicit denies.
    if (cur === true) return true
    for (let i = 0; i < path.length; i++) {
      // If a prior level wildcard collapsed to a boolean, short-circuit
      if (typeof cur === 'boolean') return cur
      const key = path[i]
      if (cur && Object.prototype.hasOwnProperty.call(cur, key)) {
        cur = cur[key]
        if (typeof cur === 'boolean') return cur // explicit allow/deny at this level wins
        continue
      }
      if (cur && Object.prototype.hasOwnProperty.call(cur, '*')) {
        cur = cur['*']
        // If wildcard is boolean, it grants/denies entire subtree
        if (typeof cur === 'boolean') return cur
        continue
      }
      if (cur && Object.prototype.hasOwnProperty.call(cur, '-1')) {
        cur = cur['-1']
        if (typeof cur === 'boolean') return cur
        continue
      }
      return false
    }
    return Boolean(cur)
  }, [state?.user?.roleActions, clientModules])

  // Initialize useAuth hook
  const { signOut: authSignOut, isSigningOut } = useAuth({ 
    createSnack 
  });

  // New function to initialize Cognito with environment variables
  async function initializeCognito() {
    const awsRegion = process.env.REACT_APP_AWS_REGION;
    const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
    const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
    const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
    
    // Dynamically set redirect URL based on environment
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const redirectURL = isLocalhost 
      ? `${window.location.protocol}//${window.location.host}`
      : (process.env.REACT_APP_REDIRECT_URL || window.location.origin);
    
    // Set AWS region for later use
    setAwsRegion(awsRegion);
    
    // Set logo values from environment variables if available
    if (process.env.REACT_APP_LOGO_URL) {
      setLogoVals({
        logoUrl: process.env.REACT_APP_LOGO_URL,
        logoHeight: process.env.REACT_APP_LOGO_HEIGHT || '40px',
        logoHeightSmall: process.env.REACT_APP_LOGO_HEIGHT_SMALL || '30px',
        logoBlackUrl: process.env.REACT_APP_LOGO_BLACK_URL || null,
        logoBlackHeight: process.env.REACT_APP_LOGO_BLACK_HEIGHT || null
      });
    }

    // Configure Amplify
    Amplify.configure({
      Auth: {
        region: awsRegion,
        userPoolId: cognitoUserPoolId,
        userPoolWebClientId: cognitoClientId,
        oauth: {
          domain: cognitoDomain,
          scope: ['email', 'openid'],
          redirectSignIn: redirectURL,
          redirectSignOut: redirectURL,
          responseType: 'code',
        },
      },
    });

    // Set federated ID name - use environment variable or default to 'Google'
    const federatedId = process.env.REACT_APP_FEDERATED_ID_NAME;
    setFederatedIdName(federatedId);
    
  }
  // Initialize dynamic title (Stage - Apix)
  useDynamicTitle();


  useEffect(() => {
    const fetchData = async () => {
      // Initialize Cognito configuration first using environment variables
      await initializeCognito();
      
      // Set up event listeners
      window.addEventListener('resize', () => calcSize());

      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
            dynamoAuditUser({token: data.signInUserSession.idToken.jwtToken, action: 'login'});
            break;
          case 'cognitoHostedUI':
            setToken('grating...');
            getToken().then((userToken) =>
              setToken(userToken.idToken.jwtToken)
            );
            break;
          case 'signOut':
            dynamoAuditUser({token: data.signInUserSession.idToken.jwtToken, action: 'logout'});
            setToken(null);
            break;
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
            console.log('Sign in failure', data);
            break;
          default:
            break;
        }
      });

      // Check authentication
      await authUser();
    };
    fetchData();
  }, []);

  const calcSize = () => {
    setAppVals({ ...appVals, appWidth: window.innerWidth });
  };

  async function dynamoAuditUser({user, token, action}) {
    await LambdaFetch(
      'audit-dynamo',
      'post',
      token,
      JSON.stringify({
        action,
        page: 'main',
        message: 'User logged out',
      }),
      'Action logged successfully'
    );
  }
  async function auditUser(user, token) {
    await LambdaFetch(
      'audit',
      'post',
      token,
      JSON.stringify({
        action: 'write',
        type: 'login',
      }),
      '',
      { login }
    );
  }
  
  async function authUser() {
    const ses = await Auth.currentSession()
      .then((session) => session)
      .catch(() => {
        setState({ ...state, doneTokenCheck: true });
        console.log('Not signed in');
      });

    if (!ses) return null;
    if (!ses.idToken) return null;

    const curJwt = ses.idToken.jwtToken;

    if (!curJwt) {
      return null;
    }

    const currentSessionUser = await getUser();
    const { email } = currentSessionUser.signInUserSession.idToken.payload;

    // After successful authentication, run clientInfo
    await clientInfo();
    getUserInfo(email, curJwt);
  }

  async function getUserInfo(email, jwt) {
    try {
    const userToken = await LambdaFetch(
      'login',
      'post',
      null,
      JSON.stringify({
        action: 'cognito-dynamo-login',
        accessToken: jwt,
        user: email,
        isLoggingIn: true,
      }),
      '',
      { login }
    );

    setState({
      ...state,
      doneTokenCheck: true,
      isAuth: true,
      user: {
        ...userToken.data.userInfo,
        accessToken: jwt,
        roleActions: userToken.data.permissions
      },
    });

  } catch(e) {
    console.log(e)
    setState({
      ...state,
      doneTokenCheck: true,
      isAuth: false,
      user: {
        accessToken: jwt,
        pagePermission: {},
        subPagePermission: {},
        roleActions: [],
      },
    });
  }
  }

  async function clientInfo() {
    const resp = await LambdaFetch(
      'login',
      'post',
      null,
      JSON.stringify({
        action: 'get-dynamo-client-values',
      }),
      '',
      { login }
    );
    // Extract non-sensitive values like logos, colors, etc.
    const logoUrl = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-url'
    )?.value;
    const logoHeight = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height'
    )?.value;
    const logoHeightSmall = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height-small'
    )?.value;
    const logoBlackUrl = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-black'
    )?.value;
    const logoBlackHeight = resp.data.clientValues.find(
      (row) => row.R_KEY === 'logo-height-black'
    )?.value;
    
    // Get system timezone setting
    const timezone = resp.data.clientValues.find(
      (row) => row.R_KEY === 'timezone'
    )?.value || resp.data.clientValues.find(
      (row) => row.R_KEY === 'timezone'
    )?.R_VALUE || 'America/New_York'; // Default to Eastern Time if not set
    
    // Get CLIENT_MODULES from frontend environment variable (set at build time)
    // This is read-only and cannot be edited by admins
    const clientModules = process.env.REACT_APP_CLIENT_MODULES || '*';

    const colorScheme = resp.data.clientValues.reduce((acc, cur) => {
      if (cur.R_KEY === 'primary_color' && cur.active) {
        acc['primaryColor'] = cur.value;
      }
      if (cur.R_KEY === 'trinary_color' && cur.active) {
        acc['trinaryColor'] = cur.value;
      }
      return acc;
    }, {});

    setClientModules(clientModules)
    setAppColor(colorScheme.primaryColor, colorScheme.trinaryColor);
    setSystemTimezone(timezone);

    // Update logo values if database has different values (optional)
    if (logoUrl && logoUrl !== process.env.REACT_APP_LOGO_URL) {
      setLogoVals(prev => ({ ...prev, logoUrl, logoHeight, logoHeightSmall, logoBlackUrl, logoBlackHeight }));
    }
    
    return null;
  }
  
  const setAppColor = (primaryColor, trinaryColor) => {
    const root = document.documentElement;
    const primary = primaryColor ? primaryColor : '#1482C5';
    const trinary = trinaryColor ? trinaryColor : '#0d6eaa';
    root.style.setProperty('--primary-color', primary);
    root.style.setProperty('--trinary-color', trinary);
    setState({ ...state, primaryAppColor: primary, trinaryAppColor: trinary });
  };

  function getToken() {
    return Auth.currentSession()
      .then((session) => session)
      .catch((err) => console.log(err));
  }

  async function getUser() {
    return Auth.currentAuthenticatedUser()
      .then((currentUser) => currentUser)
      .catch(() => console.log('Not signed in'));
  }

  // Handle sign out initiated from another tab
  const handleExternalSignOut = useCallback(async () => {
    try { await Auth.signOut({ global: true}); } catch (e) {}
    setState({
      isAuth: false,
      doneTokenCheck: true,
      isGettingProfile: false,
      isInvalidUser: false,
      user: {},
    });
    setToken(null);
    createSnack('Signed out in another tab', 'info', 3000);
  }, [createSnack]);

  // Listen for cross-tab sign-out events (BroadcastChannel + storage fallback)
  useEffect(() => {
    if (!state.isAuth) return;

    let bc;
    const onStorage = (e) => {
      if (e.key === 'apix:signout') {
        handleExternalSignOut();
      }
    };

    try {
      if ('BroadcastChannel' in window) {
        bc = new BroadcastChannel('apix-auth');
        bc.onmessage = (event) => {
          if (event?.data?.type === 'SIGN_OUT') {
            handleExternalSignOut();
          }
        };
      }
    } catch (_) {}

    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (bc) bc.close();
    };
  }, [state.isAuth, handleExternalSignOut]);

  async function signOut() {
    try {
      const result = await authSignOut(state.user, awsRegion, true);
      
      if (result.success) {
        // Reset local state after successful sign out
        setState({
          isAuth: false,
          doneTokenCheck: true,
          isGettingProfile: false,
          isInvalidUser: false,
          user: {},
        });
        setToken(null);
      }
    } catch (error) {
      console.log('error signing out: ', error);
      createSnack('Sign out failed', 'error', 3000);
    }
  }
  const login = () => {
    Auth.federatedSignIn({
      provider: federatedIdName,
    });
  };

  const getMuiTheme = () =>
    createTheme({
      palette: {
        primary: { main: '#1482C5' },
      },
      overrides: {
        MUIDataTableBodyCell: {
          root: {
            padding: '3px 6px',
          },
        },
        MUIDataTableSelectCell: {
          checked: { color: `${state.primaryAppColor} !important` },
        },
      },
    });

  if (!state.doneTokenCheck) {
    return null;
  }
  return (
    <>
      <div className="App">
        <MuiThemeProvider theme={getMuiTheme()}>
          <div style={{ margin: '0 auto' }}>
            <AppBar
              appState={{ ...state, ...appVals, ...logoVals }}
              logout={signOut}
              login={login}
              user={state.user}
              isSigningOut={isSigningOut}
            >
              <div style={{ maxWidth: '1400px', margin: 'auto' }}>
                <div>
                  {state.isAuth ? (
                    <Routes
                      login={login}
                      // userRoles={this.userRoles}
                      createSnack={createSnack}
                      credentials={{
                        ...state,
                        ...appVals,
                        clientModules,
                        login: login,
                        allow,
                        systemTimezone,
                      }}
                    />
                  ) : (
                    <LoginPage
                      federatedIdName={federatedIdName}
                      logoVals={logoVals}
                    />
                  )}
                </div>
              </div>
            </AppBar>
          </div>
        </MuiThemeProvider>
      </div>
    </>
  );
}

export default AppCognito;

